/**
 * LE CATALOGUE, ET L'ATELIER.
 *
 * Deux gestes sur un seul écran : ouvrir une aventure qui existe, ou en commander une. La commande
 * ne montre JAMAIS de sablier (§12) : elle rend un travail, et le joueur peut fermer l'onglet.
 *
 * L'IPE À LA PORTE (§3.1) est expliquée AVANT le refus : chaque aventure dit sa fourchette, et un
 * héros hors fourchette est marqué comme tel dans le sélecteur. Se faire refuser après avoir
 * cliqué, ça s'appelle une porte peinte.
 */
import { useState } from "react";

import { api, ErreurApi } from "../api/client.js";
import { useRessource } from "../api/chargement.js";
import type { AventureEnVue, CoffreEnVue, HerosEnVue, LaSemaine } from "../api/types.js";
import { Glyphe } from "../composants/Glyphe.js";
import { Message } from "../composants/Message.js";
import { Filet, Parchemin } from "../composants/Parchemin.js";
import { messageDe } from "../lexique/erreurs.js";
import { ARCHETYPES, GENRES, MODES } from "../lexique/mots.js";
import { Lien, va } from "../routeur.js";
import { useSession } from "../session.js";
import { t, tn } from "../lexique/langue.js";

/**
 * LES TROIS MODES DU §3.5, ET CE QUE CHACUN DEMANDE (§8.1, §8.2). Ils sont TOUS LES TROIS affichés,
 * même ceux qu'on n'a pas payés — désaturés d'un cran, jamais éteints (design §1) : on pèse un mode
 * de mort en le lisant, et un mode caché est un mode dont on ignore l'existence jusqu'à ce qu'un
 * autre joueur en parle.
 *
 * `option_heritage` est la clef de coffre de l'option, celle que le serveur retire au départ. Elle
 * est écrite une fois ici, et une seule : recopiée, elle finirait par différer d'une lettre, et le
 * bouton resterait éteint sur un coffre qui la contient. */
const MODES_OUVERTS = ["permadeath", "chronicle", "heritage"] as const;
const OPTION_HERITAGE = "option_heritage";

function Lancement({ aventure, heros }: { aventure: AventureEnVue; heros: HerosEnVue[] }) {
  const { compte } = useSession();
  const coffre = useRessource<CoffreEnVue>("/coffre", (charge: CoffreEnVue) => charge);
  const [herosId, poseLeHeros] = useState<string>(heros[0]?.id ?? "");
  const [mode, poseLeMode] = useState<string>("permadeath");
  const [pacte, poseLePacte] = useState(false);
  const [refus, poseLeRefus] = useState<string | null>(null);
  const [enCours, poseEnCours] = useState(false);

  const choisi = heros.find((h) => h.id === herosId) ?? null;
  // CE QUE CHAQUE MODE DEMANDE, lu du compte et du coffre. Le serveur refusera de toute façon — il
  // est seul juge —, mais le lui faire dire par un aller-retour serait une porte peinte (§3.1).
  const disponible: Record<string, boolean> = {
    permadeath: true,
    chronicle: compte?.abonnement.avantages.modeChronique ?? false,
    heritage: (coffre.donnees?.lignes ?? []).some((l) => l.objetId === OPTION_HERITAGE)
  };
  const tropBas = choisi ? choisi.ipe < aventure.ipe.min : false;
  const tropHaut = choisi ? choisi.ipe > aventure.ipe.max : false;

  async function commence() {
    poseLeRefus(null);
    poseEnCours(true);
    try {
      const { partie } = await api.poste<{ partie: { id: string } }>("/parties", {
        herosId,
        aventureId: aventure.id,
        mode,
        pacte
      });
      va(`/partie/${partie.id}`);
    } catch (erreur) {
      // Le pacte se propose au moment exact où il devient utile, pas avant : c'est le refus du
      // serveur qui le rend lisible (« vous êtes sous-équipé, entrez quand même »).
      if (erreur instanceof ErreurApi && erreur.code === "IPE_TROP_BASSE") poseLePacte(true);
      poseLeRefus(messageDe(erreur));
    } finally {
      poseEnCours(false);
    }
  }

  if (!heros.length) {
    return <p className="faible">{t("Il faut un héros vivant pour entrer.")}</p>;
  }

  return (
    <div className="pile pile--serree lancement">
      <label className="champ">
        <span className="capitales">{t("Qui part")}</span>
        <select value={herosId} onChange={(e) => poseLeHeros(e.target.value)}>
          {heros.map((h) => (
            <option key={h.id} value={h.id}>
              {t("{nom} — {archetype} (équipement {ipe})", {
                nom: h.nom,
                archetype: ARCHETYPES[h.archetype]?.nom ?? h.archetype,
                ipe: h.ipe
              })}
            </option>
          ))}
        </select>
      </label>

      <div className="rang rang--serre">
        {MODES_OUVERTS.map((m) => (
          <button
            key={m}
            type="button"
            className={mode === m ? "puce puce--choisie" : "puce"}
            onClick={() => poseLeMode(m)}
            aria-pressed={mode === m}
            title={MODES[m]?.promesse}
          >
            {MODES[m]?.nom ?? m}
          </button>
        ))}
      </div>
      <p className="faible" style={{ margin: 0 }}>
        {MODES[mode]?.promesse}
      </p>

      {!disponible[mode] ? (
        <p className="mention mention--verrou">
          <Glyphe nom="verrou" taille={14} />{" "}
          {mode === "chronicle" ? (
            <>
              {t("La Chronique demande un abonnement.")} <Lien vers="/souscription">{t("Les formules sont ici")}</Lien> {t("— et rien ne vous empêche de partir en permadeath, qui est le jeu tel qu'il a été écrit.")}
            </>
          ) : (
            <>
              {t("L'Héritage est une option, dépensée au départ, et votre coffre n'en contient pas.")}{" "}
              <Lien vers="/echoppe">{t("Elle s'achète à l'échoppe")}</Lien>.
            </>
          )}
        </p>
      ) : null}

      {tropHaut ? (
        <p className="mention mention--verrou">
          <Glyphe nom="verrou" taille={14} /> {t("Ce héros est trop bien armé pour cette histoire : elle n'aurait plus rien à lui opposer.")} <span className="faible">
            {t("({ipe} pour un plafond de {plafond}.)", { ipe: choisi?.ipe ?? 0, plafond: aventure.ipe.max })}
          </span>
        </p>
      ) : null}

      {tropBas ? (
        <label className="rang rang--serre">
          <input type="checkbox" checked={pacte} onChange={(e) => poseLePacte(e.target.checked)} />
          <span>
            {t("Entrer quand même, moins bien armé que ce que l'histoire attend — elle ne s'adoucira pas pour autant.")} <span className="faible">
              {t("({ipe} pour un seuil de {seuil}.)", { ipe: choisi?.ipe ?? 0, seuil: aventure.ipe.min })}
            </span>
          </span>
        </label>
      ) : null}

      {refus ? (
        <Message espece="refus" surFermeture={() => poseLeRefus(null)}>
          {refus}
        </Message>
      ) : null}

      <button
        type="button"
        className="bouton bouton--grand"
        disabled={enCours || tropHaut || !disponible[mode]}
        onClick={() => void commence()}
      >
        {enCours ? t("On ouvre le livre…") : t("Commencer")}
      </button>
    </div>
  );
}

function Commande({ surDemande }: { surDemande: () => void }) {
  const [genre, poseLeGenre] = useState("fantasy");
  const [titre, poseLeTitre] = useState("");
  const [ton, poseLeTon] = useState("");
  const [noeuds, poseLesNoeuds] = useState(900);
  const [refus, poseLeRefus] = useState<string | null>(null);
  const [annonce, poseLAnnonce] = useState<string | null>(null);
  const [enCours, poseEnCours] = useState(false);

  async function commande() {
    poseLeRefus(null);
    poseLAnnonce(null);
    poseEnCours(true);
    try {
      await api.poste("/aventures/generations", {
        genre,
        noeudsCibles: noeuds,
        titre: titre.trim() || undefined,
        ton: ton.trim() || undefined
      });
      poseLAnnonce(
        t("C'est parti. Comptez une demi-heure : fermez l'onglet, allez vivre, on vous préviendra à la table.")
      );
      surDemande();
    } catch (erreur) {
      poseLeRefus(messageDe(erreur));
    } finally {
      poseEnCours(false);
    }
  }

  return (
    <Parchemin variante="creux" titre={t("Commander une aventure")}>
      <p className="douce" style={{ marginTop: 0 }}>
        {t("Dites un genre, un ton, une longueur. Le monde s'écrit sans vous — sa carte, ses gens, ses chemins et ce qu'on y a caché — et vous serez le premier à le lire.")}
      </p>

      <div className="pile pile--serree">
        <label className="champ">
          <span className="capitales">{t("Genre")}</span>
          <select value={genre} onChange={(e) => poseLeGenre(e.target.value)}>
            {Object.entries(GENRES).map(([clef, nom]) => (
              <option key={clef} value={clef}>
                {nom}
              </option>
            ))}
          </select>
        </label>

        <label className="champ">
          <span className="capitales">{t("Titre (facultatif)")}</span>
          <input value={titre} onChange={(e) => poseLeTitre(e.target.value)} maxLength={80} placeholder={t("Laissez vide pour qu'il l'invente")} />
        </label>

        <label className="champ">
          <span className="capitales">{t("Ton (facultatif)")}</span>
          <input value={ton} onChange={(e) => poseLeTon(e.target.value)} maxLength={40} placeholder={t("sombre, ironique, épique…")} />
        </label>

        <label className="champ">
          <span className="capitales">
            {t("Nœuds")} <span className="nombre">{noeuds}</span>
          </span>
          <input type="range" min={200} max={1000} step={50} value={noeuds} onChange={(e) => poseLesNoeuds(Number(e.target.value))} />
        </label>
      </div>

      {refus ? <Message espece="refus" surFermeture={() => poseLeRefus(null)}>{refus}</Message> : null}
      {annonce ? <Message espece="annonce" surFermeture={() => poseLAnnonce(null)}>{annonce}</Message> : null}

      <button type="button" className="bouton" style={{ marginTop: "var(--e4)" }} disabled={enCours} onClick={() => void commande()}>
        {enCours ? t("On dépose la commande…") : t("Mettre en fabrication")}
      </button>
    </Parchemin>
  );
}

export function Aventures() {
  const catalogue = useRessource<AventureEnVue[]>("/aventures", (charge: { aventures: AventureEnVue[] }) => charge.aventures);
  // L'AVENTURE DE LA SEMAINE (§8.1) : elle est au catalogue comme les autres, mais on la NOMME —
  // sans quoi personne ne saurait laquelle des trente est celle que tout le monde joue.
  const semaine = useRessource<LaSemaine>("/public/semaine", (charge: LaSemaine) => charge);
  const heros = useRessource<HerosEnVue[]>("/heros", (charge: { heros: HerosEnVue[] }) => charge.heros);
  const [ouverte, poseLOuverte] = useState<string | null>(null);

  const vivants = (heros.donnees ?? []).filter((h) => h.status === "alive");
  const aventures = catalogue.donnees ?? [];

  return (
    <div className="page arrivee">
      <header className="entete-decran">
        <h1>{t("Les aventures")}</h1>
        <p className="douce">
          {t("Chacune attend un héros de sa taille. Trop peu armé, vous n'y verrez pas le troisième jour ; trop bien armé, elle n'aura plus rien à vous opposer.")}
        </p>
      </header>

      {semaine.donnees?.aventure ? (
        <p className="mention mention--vers" style={{ marginBottom: "var(--e5)" }}>
          <Glyphe nom="ancre" taille={14} /> <b>{semaine.donnees.aventure.titre}</b> {t("est celle de cette semaine : la même pour tout le monde, gratuite, hors quota — et la seule où l'on puisse se comparer.")} <Lien vers="/semaine">{t("Voir le registre")}</Lien>.
        </p>
      ) : null}

      <div className="aventures">
        <div className="pile pile--large">
          {aventures.map((a) => {
            const ouvert = ouverte === a.id;
            return (
              <Parchemin key={a.id} variante={ouvert ? "leve" : "pose"}>
                <div className="rang rang--espace">
                  <h2 style={{ fontSize: "var(--pas-4)" }}>{a.titre}</h2>
                  <span className="capitales">{GENRES[a.genre] ?? a.genre}</span>
                </div>

                <p className="douce" style={{ marginTop: "var(--e2)" }}>
                  {a.theme}
                </p>

                <div className="rang rang--serre faible" style={{ marginBottom: "var(--e4)" }}>
                  <span className="nombre">{tn(a.noeuds, "{n} nœud", "{n} nœuds")}</span>
                  <span>·</span>
                  <span className="nombre">{tn(a.actes.length, "{n} acte", "{n} actes")}</span>
                  <span>·</span>
                  <span className="rang rang--serre" title={t("L'indice de puissance de l'équipement : ce que l'aventure attend d'un héros à la porte.")}>
                    <Glyphe nom="epee" taille={14} /> {t("pour un héros armé")}{" "}
                    <span className="nombre">{a.ipe.min}–{a.ipe.max}</span>
                  </span>
                  {!a.catalogue ? (
                    <>
                      <span>·</span>
                      <span>{t("la vôtre")}</span>
                    </>
                  ) : null}
                </div>

                {ouvert ? (
                  <Lancement aventure={a} heros={vivants} />
                ) : (
                  <button type="button" className="bouton" onClick={() => poseLOuverte(a.id)}>
                    {t("Ouvrir")}
                  </button>
                )}
              </Parchemin>
            );
          })}

          {!aventures.length && !catalogue.enCours ? (
            <Parchemin className="vide">
              <p>
                {t("Le catalogue est vide. Commandez-en une : le temps d'un repas, elle vous attendra ici.")}
              </p>
            </Parchemin>
          ) : null}
        </div>

        <aside className="pile pile--large">
          <Commande surDemande={() => catalogue.recharge()} />
          <Filet>{t("Le pacte")}</Filet>
          <p className="faible">
            {t("Entrer moins bien armé que prévu est permis — on vous le demande en clair, et vous en portez le prix. Entrer surarmé ne l'est pas : ce ne serait plus une aventure, ce serait une visite guidée.")}
          </p>
        </aside>
      </div>
    </div>
  );
}
